<script lang="ts">
	import type {
		InputCheckboxProps,
		InputSelectProps,
		InputTextProps
	} from '$lib/elements/inputs/types';
	import Places from '../Places.svelte';
	import type { GeocodeResult } from '@app/types/Here';
	import InputCheckbox from '$lib/elements/inputs/variants/InputCheckbox.svelte';
	import InputHidden from '$lib/elements/inputs/variants/InputHidden.svelte';
	import InputText from '$lib/elements/inputs/variants/InputText.svelte';
	import { t } from 'svelte-i18n';
	import { slide } from 'svelte/transition';
	import InputSelectCountry from './InputSelectCountry.svelte';
	import { isDateAfter, parseDateISO } from '$lib/utils/date';
	import { page } from '$app/stores';
	import { onMount } from 'svelte';
	import { requiredRule } from '$lib/utils/forms';

	export let addressProps: InputTextProps = {};
	export let cityProps: InputTextProps = {};
	export let postalCodeProps: InputTextProps = {};
	export let countryProps: InputSelectProps = {};
	export let additionalAddressProps: InputTextProps = {};
	export let addressNotFoundLabel: string = $t('addressNotFound');
	export let classNames: string;
	export let additionalAddressOption: boolean = true;
	export let placesInputProps: InputTextProps;

	$: {
		if (addressNotFoundProps.checked) {
			addressProps.label = $t('kyc.steps.profile.houseNumberAndStreet');
		}
		addressNotFoundProps.disabled = addressProps.disabled;
	}

	const maxCityNameLength = 30;
	const maxPostalCodeLength = 15;

	let result: GeocodeResult;
	let addressNotFoundProps: InputCheckboxProps = {
		label: addressNotFoundLabel,
		boldLabel: true
	};

	placesInputProps = placesInputProps || {
		label: addressProps.label,
		value: addressProps?.value
	};
	$: placesInputProps.required = !addressNotFoundProps.checked;
	$: placesInputProps.disabled = addressNotFoundProps.checked || addressProps.disabled;
	$: addressNotFoundProps.checked && (placesInputProps.error = '');
	$: addressNotFoundLabel = addressNotFoundLabel ?? $t('addressNotFound');

	$: if (
		!$page?.data?.user?.status?.walletOk &&
		isDateAfter(new Date(), parseDateISO($page.data?.user?.status?.dateBlocageSouscription)) &&
		(!cityProps.value || !postalCodeProps.value || !countryProps.value)
	) {
		addressNotFoundProps.checked = true;
	}

	onMount(() => {
		if (addressProps.value && cityProps.value && postalCodeProps.value && countryProps.value)
			placesInputProps.value = `${addressProps.value}, ${postalCodeProps.value} ${cityProps.value}, ${countryProps.value}`;
	});
	$: countryProps.validation = {
		rules: [requiredRule()],
		valid: !!countryProps.value
	};

	const onSelectValue = (e: GeocodeResult) => {
		addressProps.value = [e.address.houseNumber, e.address.street]
			.filter((i) => !!i && i.length)
			.join(' ');
		addressProps = addressProps;

		cityProps.value = e.address?.city;
		if (cityProps.value === undefined) {
			cityProps.value = e.address.county;
		}
		cityProps = cityProps;

		postalCodeProps.value = e.address?.postalCode;
		if (postalCodeProps.value === undefined) {
			postalCodeProps.value = '00000';
		}
		postalCodeProps = postalCodeProps;

		countryProps.value = e.address?.countryCode;
		countryProps = countryProps;
	};
	const createValidationRule = (maxLength: number) => ({
		label: $t('forms.validators.maxLengthLimite', { values: { value: maxLength } }),
		check: (v: string) => v.length <= maxLength
	});

	const addValidationRule = (props: InputTextProps, maxlengthValue: number) => {
		if (props?.validation) {
			if (props.validation.rules) {
				props.validation.rules.push(createValidationRule(maxlengthValue));
			} else {
				props.validation = {
					rules: [createValidationRule(maxlengthValue)]
				};
			}
		}
	};
	$: addValidationRule(cityProps, maxCityNameLength);
	$: addValidationRule(postalCodeProps, maxPostalCodeLength);
</script>

<div class={`space-y-4 ${classNames}`}>
	<Places bind:inputProps={placesInputProps} onOptionClick={onSelectValue} placeTypes={'address'} />
	<InputCheckbox bind:props={addressNotFoundProps} />
	{#if !addressNotFoundProps.checked}
		<div class="hidden-inputs-block">
			<InputHidden bind:props={addressProps} />
			<InputHidden bind:props={cityProps} />
			<InputHidden bind:props={postalCodeProps} />
			<InputHidden bind:props={countryProps} />
		</div>
	{:else}
		<div class="space-y-4 ml-3" in:slide|global={{ duration: 200 }} out:slide|global={{ duration: 200 }}>
			<InputText bind:props={addressProps} />
			<InputText bind:props={cityProps} />
			<InputText bind:props={postalCodeProps} />
			<InputSelectCountry bind:props={countryProps} />
		</div>
	{/if}
	{#if additionalAddressOption}
		<InputText bind:props={additionalAddressProps} />
	{/if}
</div>
